import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import {
  PiArrowDownBold,
  PiArrowUpBold,
  PiCaretDownBold,
  PiCaretRightBold,
  PiClockClockwiseBold,
  PiPlusBold,
  PiTrashBold,
} from 'react-icons/pi';
import { FaCheck, FaXmark } from 'react-icons/fa6';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import DndSortableItem from '#/modules/commonComponents/form/DndSortableItem';
import DndSortableList from '#/modules/commonComponents/form/DndSortableList';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import {
  BOM_USAGE_NOT_USED,
  BOM_USAGE_USED,
  countUsedQty,
  createEmptyGroup,
  createEmptyPosition,
  mapPickerRowToBomMaterial,
} from '#/modules/momMgmt/settings/bom/helper/bomGroupsForm';
import { checkBomPositionDuplicate } from '#/modules/momMgmt/settings/bom/helper/bomPositionDuplicate';
import { sortBomPositionsByName } from '#/modules/momMgmt/settings/bom/helper/bomSort';

function buildPositionDuplicateKey(groupIndex, materialIndex, positionIndex) {
  return `${groupIndex}.${materialIndex}.${positionIndex}`;
}

const BomPositionRow = ({
  control,
  register,
  errors,
  setValue,
  positionsName,
  groupIndex,
  materialIndex,
  index,
  duplicateError,
  onPositionBlur,
  onRemove,
}) => {
  const usagePath = `${positionsName}.${index}.usageType`;
  const namePath = `${positionsName}.${index}.name`;
  const usageType = useWatch({ control, name: usagePath });
  const isUsed = usageType !== BOM_USAGE_NOT_USED;

  function handleToggleUsage() {
    setValue(
      usagePath,
      isUsed ? BOM_USAGE_NOT_USED : BOM_USAGE_USED,
      { shouldDirty: true, shouldValidate: true },
    );
  }

  function handleBlur(event) {
    onPositionBlur(groupIndex, materialIndex, index, event.target.value);
  }

  return (
    <div>
      <div
        className={cn(
          'grid grid-cols-[minmax(0,1fr)_2.75rem_2.25rem]',
          'items-start gap-2 px-4 py-2.5',
          duplicateError && 'bg-red-50',
        )}
      >
        <FloatingInput
          id={namePath}
          type="text"
          label={`位置名稱 #${index + 1}`}
          register={register}
          errors={errors}
          containerClassName="mb-0!"
          suppressInlineError
          onBlur={handleBlur}
          inputClassName={duplicateError ? 'text-red-600' : undefined}
        />
        <input type="hidden" {...register(usagePath)} />
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            size="icon-sm"
            variant={isUsed ? 'default' : 'outline'}
            aria-label={isUsed ? '切換為不使用' : '切換為使用'}
            className={cn(!isUsed && 'text-slate-400')}
            onClick={handleToggleUsage}
          >
            {isUsed ? <FaCheck aria-hidden /> : <FaXmark aria-hidden />}
          </Button>
        </div>
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label="刪除位置"
            onClick={onRemove}
          >
            <PiTrashBold className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
      {duplicateError ? (
        <div className="border-t border-red-200 bg-red-50 px-4 py-1.5 text-xs text-red-600">
          <div className="mb-1 font-semibold">此位置已在以下物料使用：</div>
          {duplicateError.duplicateLocations.map((location) => (
            <div
              key={`${location.groupIndex}-${location.materialIndex}-${location.positionIndex}`}
              className="mt-0.5"
            >
              <span className="font-semibold">
                •
                {' '}
                {location.groupName || '（未命名群組）'}
                {' - '}
                {location.mtrNo || location.mtrName || '—'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

BomPositionRow.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  positionsName: PropTypes.string.isRequired,
  groupIndex: PropTypes.number.isRequired,
  materialIndex: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  duplicateError: PropTypes.shape({
    isDuplicate: PropTypes.bool,
    duplicateLocations: PropTypes.arrayOf(PropTypes.shape({
      groupIndex: PropTypes.number,
      groupName: PropTypes.string,
      materialIndex: PropTypes.number,
      mtrNo: PropTypes.string,
      mtrName: PropTypes.string,
      positionIndex: PropTypes.number,
    })),
  }),
  onPositionBlur: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const BomPositionsEditor = ({
  control,
  register,
  errors,
  setValue,
  getValues,
  groupIndex,
  materialIndex,
  materialLabel,
  materialSerial,
  positionDuplicateErrors,
  onPositionBlur,
  onClearMaterialDuplicateErrors,
}) => {
  const positionsName = `groups.${groupIndex}.materials.${materialIndex}.positions`;
  const { fields, append, remove } = useFieldArray({
    control,
    name: positionsName,
  });
  const positions = useWatch({
    control,
    name: positionsName,
    defaultValue: [],
  });

  function handleResortPositions() {
    const current = getValues(positionsName) ?? [];
    setValue(positionsName, sortBomPositionsByName(current), { shouldDirty: true });
    onClearMaterialDuplicateErrors(groupIndex, materialIndex);
  }

  function handleRemovePosition(index) {
    remove(index);
    onClearMaterialDuplicateErrors(groupIndex, materialIndex);
  }

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <MaterialNameStack
            name={materialLabel || '（未命名物料）'}
            code={materialSerial}
          />
          <p className="mt-1 text-xs text-slate-600">
            數量（USED）：
            {countUsedQty(positions)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleResortPositions}
          >
            <PiClockClockwiseBold aria-hidden />
            重新排序
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => append(createEmptyPosition())}
          >
            <PiPlusBold aria-hidden />
            新增位置
          </Button>
        </div>
      </div>

      <div className="form-list form-list--window">
        <div className="form-list__head">
          <p className="font-medium">位置</p>
          <p className="text-xs text-gray-500">
            {fields.length}
            {' '}
            筆
          </p>
        </div>
        <div className="form-list__body">
          {fields.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              尚無位置，請新增。
            </p>
          ) : (
            fields.map((field, index) => (
              <BomPositionRow
                key={field.id}
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                positionsName={positionsName}
                groupIndex={groupIndex}
                materialIndex={materialIndex}
                index={index}
                duplicateError={positionDuplicateErrors[
                  buildPositionDuplicateKey(groupIndex, materialIndex, index)
                ]}
                onPositionBlur={onPositionBlur}
                onRemove={() => handleRemovePosition(index)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

BomPositionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  groupIndex: PropTypes.number.isRequired,
  materialIndex: PropTypes.number.isRequired,
  materialLabel: PropTypes.string,
  materialSerial: PropTypes.string,
  positionDuplicateErrors: PropTypes.object.isRequired,
  onPositionBlur: PropTypes.func.isRequired,
  onClearMaterialDuplicateErrors: PropTypes.func.isRequired,
};

const BomGroupBlock = ({
  control,
  register,
  errors,
  groupIndex,
  selected,
  usedMtrIdsOutsideGroup,
  onSelectMaterial,
  onRemoveGroup,
  onMoveGroupUp,
  onMoveGroupDown,
  canMoveGroupUp,
  canMoveGroupDown,
}) => {
  const materialsName = `groups.${groupIndex}.materials`;
  const { fields, append, remove } = useFieldArray({
    control,
    name: materialsName,
  });
  const materials = useWatch({
    control,
    name: materialsName,
    defaultValue: [],
  });
  const [expanded, setExpanded] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const sortedMaterialEntries = useMemo(() => (
    fields
      .map((field, materialIndex) => ({
        field,
        materialIndex,
        material: materials?.[materialIndex],
      }))
      .sort((left, right) => {
        const codeA = left.material?.mtrNo || left.material?.mtrId || '';
        const codeB = right.material?.mtrNo || right.material?.mtrId || '';
        return codeA.localeCompare(codeB, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      })
  ), [fields, materials]);

  const pickerInitialRows = useMemo(
    () => (materials ?? [])
      .filter((item) => item?.mtrId)
      .map((item) => ({
        optionMtrId: item.mtrId,
        mtrNo: item.mtrNo ?? '',
        name: item.mtrName ?? '',
        status: '',
      })),
    [materials],
  );

  function handleRemoveMaterial(materialIndex) {
    remove(materialIndex);
    if (
      selected?.groupIndex === groupIndex
      && selected?.materialIndex === materialIndex
    ) {
      onSelectMaterial(null);
      return;
    }
    if (
      selected?.groupIndex === groupIndex
      && selected?.materialIndex > materialIndex
    ) {
      onSelectMaterial({
        groupIndex,
        materialIndex: selected.materialIndex - 1,
      });
    }
  }

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const existingInGroup = new Set(
      (materials ?? []).map((item) => item?.mtrId).filter(Boolean),
    );
    const toAdd = (selectedRows ?? [])
      .map(mapPickerRowToBomMaterial)
      .filter((item) => {
        if (!item.mtrId || existingInGroup.has(item.mtrId)) {
          return false;
        }
        return !usedMtrIdsOutsideGroup.has(item.mtrId);
      });

    toAdd.forEach((item) => {
      append(item);
    });
  }

  return (
    <div className="rounded-md border border-gray-300 bg-panel">
      <div className="px-2 py-4 flex flex-wrap items-center gap-2 border-b border-slate-200">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label={expanded ? '收合群組' : '展開群組'}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? <PiCaretDownBold aria-hidden /> : <PiCaretRightBold aria-hidden />}
        </Button>
        <div className="min-w-0 flex-1">
          <FloatingInput
            id={`groups.${groupIndex}.name`}
            type="text"
            label={`群組名稱 #${groupIndex + 1}`}
            register={register}
            errors={errors}
            containerClassName="mb-0!"
            suppressInlineError
          />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="上移群組"
            disabled={!canMoveGroupUp}
            onClick={onMoveGroupUp}
          >
            <PiArrowUpBold aria-hidden />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="下移群組"
            disabled={!canMoveGroupDown}
            onClick={onMoveGroupDown}
          >
            <PiArrowDownBold aria-hidden />
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleOpenPicker}
          >
            <PiPlusBold aria-hidden />
            選料
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label="刪除群組"
            onClick={onRemoveGroup}
          >
            <PiTrashBold className="size-4" aria-hidden />
          </Button>
        </div>
      </div>

      {expanded ? (
        <ul className="divide-y divide-slate-200">
          {fields.length === 0 ? (
            <li className="px-3 py-3 text-sm text-muted-foreground">
              尚無物料，請按「選料」。
            </li>
          ) : null}
          {sortedMaterialEntries.map(({ field, materialIndex, material }) => {
            const isActive = selected?.groupIndex === groupIndex
              && selected?.materialIndex === materialIndex;
            const qty = countUsedQty(material?.positions);

            return (
              <li key={field.id}>
                <div
                  className={cn(
                    'flex items-start gap-2 px-2 py-2',
                    isActive && 'bg-primary-50',
                  )}
                >
                  <button
                    type="button"
                    className={cn(
                      'min-w-0 flex-1 rounded-md px-2 py-1 text-left',
                      'hover:bg-slate-50',
                    )}
                    onClick={() => onSelectMaterial({ groupIndex, materialIndex })}
                  >
                    <p className="truncate text-sm font-medium text-gray-800">
                      {material?.mtrName || '（未命名）'}
                    </p>
                    <div className="truncate text-xs text-muted-foreground flex justify-between">
                      <p>{material?.mtrNo || material?.mtrId || '—'}</p>
                      <p>數量:<span className="ml-1 font-medium text-primary-800">{qty}</span></p>
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label="移除物料"
                      onClick={() => handleRemoveMaterial(materialIndex)}
                    >
                      <PiTrashBold className="size-4" aria-hidden />
                    </Button>
                  </div>
                </div>
                <input
                  type="hidden"
                  {...register(`${materialsName}.${materialIndex}.mtrId`)}
                />
                <input
                  type="hidden"
                  {...register(`${materialsName}.${materialIndex}.mtrNo`)}
                />
                <input
                  type="hidden"
                  {...register(`${materialsName}.${materialIndex}.mtrName`)}
                />
              </li>
            );
          })}
        </ul>
      ) : null}

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={pickerInitialRows}
        onConfirm={handlePickerConfirm}
        title="請選擇 BOM 物料"
        description="多選後確認；已在本群組或其他群組者不重複加入。"
        includeMtrKind={false}
      />
    </div>
  );
};

BomGroupBlock.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  groupIndex: PropTypes.number.isRequired,
  selected: PropTypes.shape({
    groupIndex: PropTypes.number,
    materialIndex: PropTypes.number,
  }),
  usedMtrIdsOutsideGroup: PropTypes.instanceOf(Set).isRequired,
  onSelectMaterial: PropTypes.func.isRequired,
  onRemoveGroup: PropTypes.func.isRequired,
  onMoveGroupUp: PropTypes.func.isRequired,
  onMoveGroupDown: PropTypes.func.isRequired,
  canMoveGroupUp: PropTypes.bool.isRequired,
  canMoveGroupDown: PropTypes.bool.isRequired,
};

const BomGroupsPositionsEditor = ({
  control,
  register,
  errors,
  setValue,
  getValues,
}) => {
  const { fields, append, remove, replace, move } = useFieldArray({
    control,
    name: 'groups',
  });
  const groups = useWatch({
    control,
    name: 'groups',
    defaultValue: [],
  });

  const [selected, setSelected] = useState(null);
  const [positionDuplicateErrors, setPositionDuplicateErrors] = useState({});

  const selectedMaterial = useMemo(() => {
    if (selected == null) {
      return null;
    }
    return groups?.[selected.groupIndex]?.materials?.[selected.materialIndex] ?? null;
  }, [groups, selected]);

  function usedMtrIdsOutside(groupIndex) {
    const ids = new Set();
    (groups ?? []).forEach((group, index) => {
      if (index === groupIndex) {
        return;
      }
      (group?.materials ?? []).forEach((material) => {
        if (material?.mtrId) {
          ids.add(material.mtrId);
        }
      });
    });
    return ids;
  }

  function clearMaterialDuplicateErrors(groupIndex, materialIndex) {
    setPositionDuplicateErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${groupIndex}.${materialIndex}.`)) {
          delete next[key];
        }
      });
      return next;
    });
  }

  function handleSelectMaterial(next) {
    if (next) {
      const positionsPath = `groups.${next.groupIndex}.materials.${next.materialIndex}.positions`;
      const current = getValues(positionsPath) ?? [];
      setValue(positionsPath, sortBomPositionsByName(current), { shouldDirty: false });
      clearMaterialDuplicateErrors(next.groupIndex, next.materialIndex);
    }
    setSelected(next);
  }

  function handlePositionBlur(groupIndex, materialIndex, positionIndex, positionName) {
    const key = buildPositionDuplicateKey(groupIndex, materialIndex, positionIndex);
    if (!positionName?.trim()) {
      setPositionDuplicateErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    const result = checkBomPositionDuplicate({
      groups: getValues('groups'),
      positionName,
      exclude: { groupIndex, materialIndex, positionIndex },
    });

    if (result.isDuplicate) {
      setPositionDuplicateErrors((prev) => ({ ...prev, [key]: result }));
      return;
    }

    setPositionDuplicateErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleAddGroup() {
    append(createEmptyGroup({ name: `群組 ${fields.length + 1}` }));
  }

  function handleRemoveGroup(groupIndex) {
    remove(groupIndex);
    if (selected?.groupIndex === groupIndex) {
      setSelected(null);
      return;
    }
    if (selected?.groupIndex > groupIndex) {
      setSelected({
        groupIndex: selected.groupIndex - 1,
        materialIndex: selected.materialIndex,
      });
    }
  }

  function handleMoveGroup(from, to) {
    if (to < 0 || to >= fields.length) {
      return;
    }
    move(from, to);
    if (selected?.groupIndex === from) {
      setSelected({ ...selected, groupIndex: to });
    } else if (selected?.groupIndex === to) {
      setSelected({ ...selected, groupIndex: from });
    }
  }

  function handleReorderGroups(nextFields) {
    const groupByFieldId = new Map();
    fields.forEach((field, index) => {
      groupByFieldId.set(field.id, groups?.[index]);
    });

    const nextGroups = nextFields.map((field) => groupByFieldId.get(field.id) ?? createEmptyGroup());
    replace(nextGroups);

    if (selected == null) {
      return;
    }

    const selectedFieldId = fields[selected.groupIndex]?.id;
    const newGroupIndex = nextFields.findIndex((field) => field.id === selectedFieldId);
    if (newGroupIndex < 0) {
      setSelected(null);
      return;
    }

    setSelected({
      groupIndex: newGroupIndex,
      materialIndex: selected.materialIndex,
    });
  }

  return (
    <section
      className={cn(
        'form-section @container/form-section',
        'pt-4 border-t border-gray-400',
      )}
    >
      <div className="form-section__header">
        <h2 className="form-block__title mb-0! mr-2">物料位置</h2>
        <Button type="button" size="sm" onClick={handleAddGroup}>
          <PiPlusBold aria-hidden />
          新增群組
        </Button>
      </div>

      {errors?.groups?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.groups.message}</p>
      ) : null}

      <div
        className={cn(
          'mt-2.5 min-h-100',
          'grid grid-cols-1 gap-5',
          '@4xl/sheet:grid-cols-[1fr_1px_1fr]',
        )}
      >
        <div className="create-edit-panel-scroll space-y-3">
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              尚無群組。可先儲存空 groups，或新增群組後選料。
            </p>
          ) : (
            <DndSortableList
              items={fields}
              getItemId={(item) => item.id}
              onReorder={handleReorderGroups}
              className="space-y-3"
            >
              {fields.map((field, groupIndex) => (
                <DndSortableItem
                  key={field.id}
                  id={field.id}
                  className="items-stretch border-0 bg-transparent p-0 shadow-none"
                >
                  <BomGroupBlock
                    control={control}
                    register={register}
                    errors={errors}
                    groupIndex={groupIndex}
                    selected={selected}
                    usedMtrIdsOutsideGroup={usedMtrIdsOutside(groupIndex)}
                    onSelectMaterial={handleSelectMaterial}
                    onRemoveGroup={() => handleRemoveGroup(groupIndex)}
                    onMoveGroupUp={() => handleMoveGroup(groupIndex, groupIndex - 1)}
                    onMoveGroupDown={() => handleMoveGroup(groupIndex, groupIndex + 1)}
                    canMoveGroupUp={groupIndex > 0}
                    canMoveGroupDown={groupIndex < fields.length - 1}
                  />
                </DndSortableItem>
              ))}
            </DndSortableList>
          )}
        </div>

        <div
          className={cn(
            'border-b-2 border-dashed border-gray-400',
            '@4xl/sheet:border-b-0 @4xl/sheet:border-l-2',
          )}
          aria-hidden
        />

        <div className="create-edit-panel-scroll">
          {selected == null || !selectedMaterial ? (
            <p className="text-sm font-semibold text-gray-700">請先選擇物料</p>
          ) : (
            <BomPositionsEditor
              key={`${selected.groupIndex}-${selected.materialIndex}`}
              control={control}
              register={register}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              groupIndex={selected.groupIndex}
              materialIndex={selected.materialIndex}
              materialLabel={selectedMaterial.mtrName ?? ''}
              materialSerial={selectedMaterial.mtrNo ?? ''}
              positionDuplicateErrors={positionDuplicateErrors}
              onPositionBlur={handlePositionBlur}
              onClearMaterialDuplicateErrors={clearMaterialDuplicateErrors}
            />
          )}
        </div>
      </div>
    </section>
  );
};

BomGroupsPositionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
};

export default BomGroupsPositionsEditor;
